/**
 * Merchant Role Class
 * Implements merchant-specific behaviors and permissions
 */

import { BaseRole, NavigationItem, QuickAction, DashboardMetric, ValidationResult } from './BaseRole';

export class MerchantRole extends BaseRole {
  get roleName(): string {
    return 'MERCHANT';
  }

  get displayName(): string {
    return 'Merchant';
  }

  getDefaultRoute(): string {
    const merchant = this.user.merchantProfile;
    
    // If not onboarded, go to onboarding
    if (!merchant?.completedOnboarding) {
      return '/merchant/onboarding-choice';
    }
    
    // Otherwise go to merchant tools
    return '/merchant-tools';
  }

  getNavigationItems(): NavigationItem[] {
    const items: NavigationItem[] = [];
    
    // Only show merchant tools if onboarded
    if (this.user.isMerchantOnboarded) {
      items.push({
        label: 'Dashboard',
        path: '/merchant-tools',
        icon: 'LayoutDashboard'
      });
      
      items.push({
        label: 'Products',
        path: '/products',
        icon: 'Package',
        children: [
          { label: 'All Products', path: '/products' },
          { label: 'Add Product', path: '/products/add' },
          { label: 'Categories', path: '/products/categories' }
        ]
      });
      
      items.push({
        label: 'Transactions',
        path: '/transactions',
        icon: 'Receipt',
        badge: this.getPendingTransactionCount()
      });
      
      items.push({
        label: 'Analytics',
        path: '/merchant/analytics',
        icon: 'TrendingUp'
      });
      
      items.push({
        label: 'Settings',
        path: '/merchant/settings',
        icon: 'Settings',
        children: [
          { label: 'Business Profile', path: '/profile/merchant' },
          { label: 'Payment Settings', path: '/merchant/payment-settings' },
          { label: 'API Keys', path: '/merchant/api-keys' }
        ]
      });
    } else {
      // Show onboarding if not completed
      items.push({
        label: 'Complete Setup',
        path: '/merchant/onboarding-choice',
        icon: 'Sparkles'
      });
    }
    
    return items;
  }

  canPerformAction(action: string): boolean {
    const permissions: Record<string, boolean> = {
      'create_product': this.user.isMerchantOnboarded,
      'edit_product': this.user.isMerchantOnboarded,
      'delete_product': this.user.isMerchantOnboarded,
      'view_analytics': this.user.isMerchantOnboarded,
      'manage_api_keys': this.user.isMerchantOnboarded,
      'create_invoice': this.user.isMerchantOnboarded,
      'send_announcement': this.user.isMerchantOnboarded,
      'manage_followers': this.user.isMerchantOnboarded,
      'update_business_profile': true,
      'view_transactions': true,
      'generate_qr_code': this.user.isMerchantOnboarded,
      'create_payment_link': this.user.isMerchantOnboarded,
      'export_data': this.user.isMerchantOnboarded,
      'manage_catalog': this.user.isMerchantOnboarded,
      'set_business_hours': true,
      'upload_files': true
    };
    
    return permissions[action] || false;
  }

  getSettings(): Record<string, any> {
    const merchant = this.user.merchantProfile;
    
    return {
      acceptCreditCards: merchant?.settings?.acceptCreditCards || false,
      acceptPayPal: merchant?.settings?.acceptPayPal || false,
      acceptBankTransfers: merchant?.settings?.acceptBankTransfers || false,
      requireKYC: merchant?.settings?.requireKYC || false,
      autoAcceptPayments: merchant?.settings?.autoAcceptPayments || true,
      preferredCurrency: merchant?.preferredCurrency || 'USD',
      acceptedCurrencies: merchant?.acceptedCurrencies || ['USD']
    };
  }

  getBadgeColor(): string {
    if (!this.user.isMerchantOnboarded) return 'warning';
    if (this.user.merchantProfile?.verification?.isVerified) return 'success';
    return 'default';
  }

  getIconName(): string {
    return 'Store';
  }

  getWelcomeMessage(): string {
    const businessName = this.user.merchantBusinessName;
    if (businessName) {
      return `Welcome back to ${businessName}!`;
    }
    return `Welcome back, ${this.user.displayName}!`;
  }

  getQuickActions(): QuickAction[] {
    const actions: QuickAction[] = [];
    
    if (!this.user.isMerchantOnboarded) {
      actions.push({
        id: 'complete_setup',
        title: 'Complete Setup',
        description: 'Finish setting up your merchant profile',
        icon: 'Sparkles',
        action: '/merchant/onboarding-choice',
        variant: 'primary'
      });
      return actions;
    }
    
    actions.push({
      id: 'create_invoice',
      title: 'Create Invoice',
      description: 'Send a new invoice to a customer',
      icon: 'FileText',
      action: '/create-invoice'
    });
    
    actions.push({
      id: 'add_product',
      title: 'Add Product',
      description: 'Add a new product to your catalog',
      icon: 'Plus',
      action: '/products/add'
    });
    
    actions.push({
      id: 'view_analytics',
      title: 'View Analytics',
      description: 'Check your business performance',
      icon: 'TrendingUp',
      action: '/merchant/analytics'
    });
    
    actions.push({
      id: 'generate_qr',
      title: 'Generate QR Code',
      description: 'Create a payment QR code',
      icon: 'QrCode',
      action: () => {
        // This would trigger QR generation modal
        return 'generate_qr_modal';
      }
    });
    
    return actions;
  }

  getDashboardMetrics(): DashboardMetric[] {
    const metrics: DashboardMetric[] = [];
    const merchant = this.user.merchantProfile;
    
    if (!merchant) return metrics;
    
    // These would normally come from API
    metrics.push({
      id: 'total_revenue',
      label: 'Total Revenue',
      value: '$0',
      icon: 'DollarSign',
      color: 'green'
    });
    
    metrics.push({
      id: 'active_customers',
      label: 'Active Customers',
      value: 0,
      icon: 'Users',
      color: 'blue'
    });
    
    metrics.push({
      id: 'pending_tokens',
      label: 'Pending Tokens',
      value: 0,
      icon: 'Clock',
      color: 'yellow'
    });
    
    metrics.push({
      id: 'products',
      label: 'Products',
      value: merchant.catalog?.length || 0,
      icon: 'Package',
      color: 'purple'
    });
    
    return metrics;
  }

  validate(): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (!this.user.email) {
      warnings.push('Email address is recommended for merchant accounts');
    }
    
    if (!this.user.profile.phone) {
      warnings.push('Phone number is recommended for customer communication');
    }
    
    const merchant = this.user.merchantProfile;
    if (merchant) {
      if (!merchant.businessName) {
        errors.push('Business name is required');
      }
      
      if (!merchant.address) {
        warnings.push('Business address helps customers find you');
      }
      
      if (!merchant.phone && !merchant.email) {
        errors.push('At least one contact method is required');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  // ===== Merchant-specific helper methods =====

  private getPendingTransactionCount(): number {
    // This would normally come from API or state
    return 0;
  }

  canPublishProfile(): boolean {
    const validation = this.validate();
    return validation.isValid && this.user.isMerchantOnboarded;
  }

  getOnboardingProgress(): number {
    const merchant = this.user.merchantProfile;
    if (!merchant) return 0;
    
    let completed = 0;
    let total = 8;
    
    if (merchant.businessName) completed++;
    if (merchant.businessType) completed++;
    if (merchant.description) completed++;
    if (merchant.address) completed++;
    if (merchant.phone || merchant.email) completed++;
    if (merchant.businessHours) completed++;
    if (merchant.logo || merchant.photos?.length) completed++;
    if (merchant.completedOnboarding) completed++;
    
    return Math.round((completed / total) * 100);
  }
}
